/**
 * Serviço de Transcrição de Áudio
 * Converte áudio para texto usando API de transcrição
 */

// Edge Function URL - chave OpenAI fica no servidor (seguro)
const EDGE_AI_URL = import.meta.env.VITE_EDGE_AI_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export interface TranscriptionResult {
  text: string;
  language?: string;
  duration?: number;
}

/**
 * Transcreve um arquivo de áudio usando a API Whisper da OpenAI
 * @param audioBlob - Blob do áudio gravado
 * @param language - Idioma opcional (pt, en, etc.)
 * @returns Texto transcrito
 */
export async function transcribeAudio(
  audioBlob: Blob,
  language: string = 'pt'
): Promise<TranscriptionResult> {
  try {
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.webm');
    formData.append('model', 'whisper-1');
    formData.append('language', language);

    if (!EDGE_AI_URL) {
      throw new Error('Edge Function não configurada. Configure VITE_EDGE_AI_URL no arquivo .env');
    }

    const headers: Record<string, string> = {};
    if (SUPABASE_ANON_KEY) {
      headers['apikey'] = SUPABASE_ANON_KEY;
      headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
    }

    const response = await fetch(EDGE_AI_URL, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Erro na transcrição: ${error}`);
    }

    const result = await response.json();
    
    return {
      text: result.text,
      language: result.language,
      duration: result.duration
    };
  } catch (error) {
    console.error('Erro ao transcrever áudio:', error);
    throw error;
  }
}

/**
 * Grava áudio do microfone do usuário
 * @param onDataAvailable - Callback com o blob do áudio gravado
 * @returns Função para parar a gravação
 */
export async function startRecording(
  onDataAvailable: (blob: Blob) => void
): Promise<() => void> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 44100
      } 
    });

    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'audio/webm;codecs=opus'
    });

    const audioChunks: BlobPart[] = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      onDataAvailable(audioBlob);
      
      // Para todas as tracks do stream
      stream.getTracks().forEach(track => track.stop());
    };

    mediaRecorder.start();

    // Retorna função para parar a gravação
    return () => {
      if (mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
    };
  } catch (error) {
    console.error('Erro ao iniciar gravação:', error);
    throw new Error('Não foi possível acessar o microfone. Verifique as permissões.');
  }
}
